import { createContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react';

type ConfirmVariant = 'danger' | 'warning' | 'info';

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  input?: boolean;
  inputPlaceholder?: string;
  inputRequired?: boolean;
}

type ConfirmResult = boolean | string | null;

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<ConfirmResult>;
}

export const ConfirmContext = createContext<ConfirmContextValue | null>(null);

const VARIANT_CONFIG: Record<ConfirmVariant, { bg: string; iconBg: string; iconColor: string; btnBg: string; btnHover: string; icon: ReactNode }> = {
  danger: {
    bg: '#fef2f2', iconBg: '#fee2e2', iconColor: '#dc2626', btnBg: '#dc2626', btnHover: '#b91c1c',
    icon: <Trash2 size={28} />,
  },
  warning: {
    bg: '#fffbeb', iconBg: '#fef3c7', iconColor: '#d97706', btnBg: '#d97706', btnHover: '#b45309',
    icon: <AlertTriangle size={28} />,
  },
  info: {
    bg: '#eff6ff', iconBg: '#dbeafe', iconColor: '#2563eb', btnBg: '#2563eb', btnHover: '#1d4ed8',
    icon: <HelpCircle size={28} />,
  },
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [inputValue, setInputValue] = useState('');
  const resolveRef = useRef<((value: ConfirmResult) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<ConfirmResult> => {
    setOptions(opts);
    setInputValue('');
    return new Promise(resolve => { resolveRef.current = resolve; });
  }, []);

  const handleConfirm = () => {
    if (options?.input) {
      if (options.inputRequired && !inputValue.trim()) return;
      resolveRef.current?.(inputValue);
    } else {
      resolveRef.current?.(true);
    }
    setOptions(null);
  };

  const handleCancel = () => {
    resolveRef.current?.(options?.input ? null : false);
    setOptions(null);
  };

  const cfg = options ? VARIANT_CONFIG[options.variant || 'info'] : VARIANT_CONFIG.info;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      {options && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 10000,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          animation: 'confirmFadeIn 0.2s ease-out',
        }}
        onClick={handleCancel}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 20, padding: '32px',
              maxWidth: 440, width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              animation: 'confirmSlideUp 0.2s ease-out',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: cfg.iconBg, color: cfg.iconColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              {cfg.icon}
            </div>

            <h3 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>
              {options.title}
            </h3>

            {options.message && (
              <p style={{ textAlign: 'center', margin: '0 0 20px', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.5 }}>
                {options.message}
              </p>
            )}

            {options.input && (
              <textarea
                autoFocus
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder={options.inputPlaceholder || 'Saisissez votre réponse...'}
                style={{
                  width: '100%', padding: 12, border: '1px solid #cbd5e1',
                  borderRadius: 10, fontSize: '0.9rem', resize: 'vertical',
                  minHeight: 80, marginBottom: 20, outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={handleCancel} style={{
                padding: '12px 24px', background: '#f1f5f9', color: '#475569',
                border: '1px solid #e2e8f0', borderRadius: 10, fontSize: '0.9rem',
                fontWeight: 600, cursor: 'pointer', flex: 1,
              }}>
                {options.cancelLabel || 'Annuler'}
              </button>
              <button onClick={handleConfirm} style={{
                padding: '12px 24px', background: cfg.btnBg, color: 'white',
                border: 'none', borderRadius: 10, fontSize: '0.9rem',
                fontWeight: 600, cursor: 'pointer', flex: 1,
                opacity: options.input && options.inputRequired && !inputValue.trim() ? 0.5 : 1,
              }}>
                {options.confirmLabel || 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes confirmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes confirmSlideUp { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </ConfirmContext.Provider>
  );
}
